import { Button, Flex, Paper, TextInput, Text, Loader } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import {
  CreateVentRequest,
  useCreateVentMutation,
} from "../slices/api/ventsApi";
import { DateTimePicker } from "@mantine/dates";
import {
  initialize,
  MinimalVent,
  updateField,
} from "../slices/form/createVent";
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { convertToIso8601WithLocalTimezone } from "../shared/dates";
import { getLinkToVentConfig } from "../shared/links";

const getInitialDate = (): string => {
  // Define the initial state using that type
  // Default to starting at 6pm 4 weeks from today.
  let now = new Date();
  let when6pm = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 28,
    18,
  );
  return when6pm.toISOString();
};

interface Props {
  onClose: () => void;
}
const CreateVent = (props: Props) => {
  const [_, navigate] = useLocation();
  const { data: minimalVent } = useAppSelector(
    (state: RootState) => state.createVentForm,
  );
  const [createVent] = useCreateVentMutation();
  const nameRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(initialize({ starts: getInitialDate() } as MinimalVent));
  }, []);

  const handleClick = async () => {
    if (minimalVent === undefined) {
      console.error("Event is undefined but should not be");
      return;
    }
    if (!minimalVent.name) {
      displayErrorMessage("Event name is required.");
      if (nameRef.current) nameRef.current.focus();
      return;
    }

    const request: CreateVentRequest = {
      name: minimalVent.name,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // use host's timezone
      starts: minimalVent.starts,
    };

    try {
      const response = await createVent(request).unwrap();
      navigate(getLinkToVentConfig(response.id));
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };
  if (!minimalVent) {
    return <Loader color="blue" />;
  }

  return (
    <>
      <Paper>
        <Text size="lg">Add Event</Text>
        <TextInput
          ref={nameRef}
          required
          mt="md"
          label="Event name"
          value={minimalVent.name ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "name",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <DateTimePicker
          timePickerProps={{ format: "12h" }}
          required
          mt="md"
          label="Starts at"
          placeholder="Pick start date and time"
          valueFormat="DD MMM YYYY hh:mm A"
          value={new Date(minimalVent.starts)}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "starts",
                value:
                  convertToIso8601WithLocalTimezone(event ?? undefined) ??
                  minimalVent.starts,
              }),
            );
          }}
        />
        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button mt="md" onClick={handleClick}>
            Save
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default CreateVent;
